import { useId, useState, type ReactNode } from 'react';
import { Avatar, Button } from './basics.js';
import { Icon, type IconName } from './icon.js';
import { Drawer } from './overlay.js';

export interface NavItem {
  id: string;
  label: string;
  href: string;
  icon?: IconName;
  /** Open work: waiting conversations, open tasks, items to review. */
  count?: number;
  /** How the count reads when it is not exact ("50+"); the number is still `count`. */
  countText?: string;
  /**
   * ADR 252: the page is not on the clinic's plan. A lock shows after the label and
   * screen readers hear this text ("on the Clinic plan"); the page explains the upgrade.
   */
  locked?: string;
}

/** Router-agnostic link: apps pass their router's Link. */
export type RenderLink = (props: {
  href: string;
  className?: string;
  title?: string;
  'aria-current'?: 'page';
  onClick?: () => void;
  children: ReactNode;
}) => ReactNode;

const plainLink: RenderLink = ({ href, children, ...rest }) => (
  <a href={href} {...rest}>
    {children}
  </a>
);

export interface SidebarSection {
  label: string;
  items: NavItem[];
  /** Keep the group's name for screen readers only (the canvas shows no label on the first group). */
  hideLabel?: boolean;
}

export interface SidebarProps {
  /** Clinic name, or "Novations" in the back office. */
  org: string;
  /** Line under the name: "Amelia · Manager", or the clinic's locations. */
  who?: string;
  sections: SidebarSection[];
  /**
   * One navigation landmark with this name holding every group (ADR 167 §5).
   * Without it, each section is its own landmark named by its label (the older layout).
   */
  navLabel?: string;
  /** Pinned at the foot of the navigation, above the person: Settings. */
  footItems?: NavItem[];
  /** The signed-in person at the very foot; a link to their profile when `href` is set. */
  person?: { name: string; role: string; href?: string; photoUrl?: string | null };
  currentId?: string;
  /** Small print under everything; defaults to "Powered by Novations AI" in the older layout only. */
  footer?: ReactNode;
  /**
   * The clinic's logo (ADR 183, D43): a short-lived image link shown in the brand
   * mark instead of the initial. Decorative (the clinic name is next to it); a
   * missing or failed image shows the initial.
   */
  logoUrl?: string;
  /**
   * ADR 185: where the clinic's logo and name lead for the people who may change them
   * (Settings › Business › Logo and brand colour); without it the brand is not a link.
   */
  brandHref?: string;
  /**
   * D49 (plan 52): the logo and name open the logo dialog on the current page
   * instead of leaving it; the brand becomes a button. With it `brandHref` is
   * only the words of the accessible name.
   */
  onBrandClick?: () => void;
  renderLink?: RenderLink;
  onNavigate?: () => void;
}

function NavLink({
  item,
  currentId,
  renderLink,
  onNavigate,
}: {
  item: NavItem;
  currentId: string | undefined;
  renderLink: RenderLink;
  onNavigate: (() => void) | undefined;
}) {
  return (
    <>
      {renderLink({
        href: item.href,
        ...(item.id === currentId && { 'aria-current': 'page' as const }),
        ...(onNavigate && { onClick: onNavigate }),
        children: (
          <>
            {item.icon && <Icon name={item.icon} />}
            {item.label}
            {item.locked ? (
              <>
                <Icon name="lock" />
                <span className="sr-only">, {item.locked}</span>
              </>
            ) : null}
            {item.count ? (
              <span className="count">
                {item.countText ?? item.count}
                <span className="sr-only"> open</span>
              </span>
            ) : null}
          </>
        ),
      })}
    </>
  );
}

/**
 * Workspace navigation. Pages a role cannot open are not passed in (Sidebar
 * README). With `navLabel` it is the ADR 167 sidebar: white, 232px, the
 * groups in one landmark, Settings pinned at the foot and the signed-in
 * person under it (D25).
 */
export function Sidebar({
  org,
  who,
  sections,
  navLabel,
  footItems = [],
  person,
  currentId,
  footer,
  renderLink = plainLink,
  onNavigate,
  logoUrl,
  brandHref,
  onBrandClick,
}: SidebarProps) {
  const groupId = useId();
  // The link that failed to load; a new link (re-signed or a new logo) is tried again.
  const [failedLogo, setFailedLogo] = useState<string | undefined>(undefined);
  const logo = logoUrl && failedLogo !== logoUrl ? logoUrl : undefined;
  const small = footer === undefined ? (navLabel ? null : 'Powered by Novations AI') : footer;
  return (
    <aside className="side" aria-label="Navigation">
      {(() => {
        const content = (
          <>
            <span className={logo ? 'logo logo-img' : 'logo'} aria-hidden="true">
              {logo ? (
                <img src={logo} alt="" onError={() => setFailedLogo(logo)} />
              ) : (
                org.trim().charAt(0).toUpperCase()
              )}
            </span>
            <span>
              <b>{org}</b>
              {who && <small>{who}</small>}
              {(brandHref || onBrandClick) && (
                <span className="sr-only">: change logo and colour</span>
              )}
            </span>
            {(brandHref || onBrandClick) && (
              <span className="brand-edit" aria-hidden="true">
                <Icon name="edit" size={16} />
              </span>
            )}
          </>
        );
        if (onBrandClick)
          return (
            <button
              type="button"
              className="brand brand-link"
              title="Change logo and colour"
              onClick={() => {
                onNavigate?.();
                onBrandClick();
              }}
            >
              {content}
            </button>
          );
        return brandHref ? (
          renderLink({
            href: brandHref,
            className: 'brand brand-link',
            title: 'Change logo and colour',
            ...(onNavigate && { onClick: onNavigate }),
            children: content,
          })
        ) : (
          <div className="brand">{content}</div>
        );
      })()}
      {navLabel ? (
        <nav className="nav nav-main" aria-label={navLabel}>
          {sections.map((section, i) => (
            <div
              className="nav-group"
              role="group"
              aria-labelledby={`${groupId}-${i}`}
              key={section.label}
            >
              <div className={section.hideLabel ? 'sr-only' : 'navlabel'} id={`${groupId}-${i}`}>
                {section.label}
              </div>
              {section.items.map((item) => (
                <NavLink
                  key={item.id}
                  item={item}
                  currentId={currentId}
                  renderLink={renderLink}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          ))}
          {footItems.length > 0 && (
            <div className="nav-group nav-foot">
              {footItems.map((item) => (
                <NavLink
                  key={item.id}
                  item={item}
                  currentId={currentId}
                  renderLink={renderLink}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          )}
        </nav>
      ) : (
        sections.map((section) => (
          <nav className="nav" key={section.label} aria-label={section.label}>
            <div className="nav-group">
              <div className="navlabel">{section.label}</div>
              {section.items.map((item) => (
                <NavLink
                  key={item.id}
                  item={item}
                  currentId={currentId}
                  renderLink={renderLink}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          </nav>
        ))
      )}
      {person &&
        (person.href ? (
          renderLink({
            href: person.href,
            className: 'side-person',
            ...(currentId === 'profile' && { 'aria-current': 'page' as const }),
            ...(onNavigate && { onClick: onNavigate }),
            children: (
              <Person name={person.name} role={person.role} photoUrl={person.photoUrl} link />
            ),
          })
        ) : (
          <div className="side-person">
            <Person name={person.name} role={person.role} photoUrl={person.photoUrl} />
          </div>
        ))}
      {small && <div className="foot">{small}</div>}
    </aside>
  );
}

function Person({
  name,
  role,
  photoUrl,
  link = false,
}: {
  name: string;
  role: string;
  /** Feature 95: the person's photo (a short-lived link); initials when missing or failed. */
  photoUrl?: string | null | undefined;
  link?: boolean;
}) {
  return (
    <>
      <Avatar name={name} src={photoUrl} />
      <span>
        {link && <span className="sr-only">Your profile: </span>}
        <b>{name}</b>
        <span className="sr-only">, </span>
        <small>{role}</small>
      </span>
    </>
  );
}

export interface SideNavGroup {
  label: string;
  /** `icon` (D56) shows in the phone's Settings list only. */
  items: { id: string; label: string; href: string; icon?: IconName }[];
}

/**
 * Settings-style sub-navigation (ADR 126 D14): groups of links on the left,
 * the section on the right with its own heading. Under 760px the groups
 * become one horizontal strip above the content. The current item carries
 * `aria-current="page"`.
 */
export function SideNav({
  label,
  groups,
  currentId,
  renderLink = plainLink,
  children,
}: {
  label: string;
  groups: SideNavGroup[];
  currentId: string;
  renderLink?: RenderLink;
  children: ReactNode;
}) {
  return (
    <div className="subnav-layout">
      <nav className="subnav" aria-label={label}>
        {groups.map((g) => (
          <div key={g.label} className="subnav-group">
            <div className="navlabel">{g.label}</div>
            <ul>
              {g.items.map((item) => (
                <li key={item.id}>
                  {renderLink({
                    href: item.href,
                    ...(item.id === currentId && { 'aria-current': 'page' as const }),
                    children: item.label,
                  })}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      <div className="subnav-content">{children}</div>
    </div>
  );
}

/** Top strip: menu on the phone, the page title, and the account actions. */
export function Topbar({
  onMenu,
  title,
  brand,
  children,
}: {
  onMenu?: () => void;
  /** The current page, shown on phones where the sidebar is hidden. */
  title?: string | undefined;
  /** D56: on phones with a tab bar, the clinic's mark in place of the menu button and title. */
  brand?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <header className="topbar">
      <div className="row">
        {onMenu ? (
          <Button className="menu-btn" small icon="menu" onClick={onMenu} aria-label="Open menu">
            Menu
          </Button>
        ) : null}
        {brand ? <span className="topbar-brand">{brand}</span> : null}
        {title && <span className="topbar-title">{title}</span>}
      </div>
      <div className="row">{children}</div>
    </header>
  );
}

/** D56: the phone's bottom navigation: four pages and "More". */
export interface ShellTabs {
  items: NavItem[];
  currentId: string;
  renderLink?: RenderLink;
  /** Label of the last tab, which opens every other page in a sheet. */
  moreLabel?: string;
  /** The clinic's mark beside the topbar's actions on phones. */
  brand?: ReactNode;
}

export function TabBar({
  tabs,
  moreOpen,
  onMore,
}: {
  tabs: ShellTabs;
  moreOpen: boolean;
  onMore: () => void;
}) {
  const renderLink = tabs.renderLink ?? plainLink;
  const onTab = tabs.items.some((t) => t.id === tabs.currentId);
  return (
    <nav className="tabbar" aria-label="Main">
      {tabs.items.map((item) =>
        renderLink({
          href: item.href,
          className: 'tab',
          ...(item.id === tabs.currentId && !moreOpen && { 'aria-current': 'page' as const }),
          children: (
            <>
              <span className="tab-pill" aria-hidden="true">
                {item.icon && <Icon name={item.icon} />}
                {item.count ? (
                  <span className="tab-badge">
                    {item.countText ?? (item.count > 99 ? '99+' : item.count)}
                  </span>
                ) : null}
              </span>
              <span className="tab-label">
                {item.label}
                {item.count ? (
                  <span className="sr-only">, {item.countText ?? item.count} open</span>
                ) : null}
              </span>
            </>
          ),
        }),
      )}
      <button
        type="button"
        className="tab"
        aria-haspopup="dialog"
        aria-expanded={moreOpen}
        {...((moreOpen || !onTab) && { 'data-active': '' })}
        onClick={onMore}
      >
        <span className="tab-pill" aria-hidden="true">
          <Icon name="more" />
        </span>
        <span className="tab-label">{tabs.moreLabel ?? 'More'}</span>
      </button>
    </nav>
  );
}

/**
 * Sidebar + topbar + main. Under 760px the sidebar moves into a drawer opened
 * from the topbar; with `tabs` (D56) phones get a bottom tab bar instead, and
 * "More" opens the sidebar as a sheet from the bottom. The skip link is the
 * first focusable element.
 */
export function AppShell({
  sidebar,
  topbar,
  title,
  theme = 'clinic',
  navOpen,
  onNavOpenChange,
  tabs,
  children,
}: {
  /** `place` is `sheet` when drawn in the phone's "More" sheet. */
  sidebar: (onNavigate?: () => void, place?: 'side' | 'sheet') => ReactNode;
  topbar?: ReactNode;
  /** Current page title for the topbar. */
  title?: string;
  theme?: 'clinic' | 'novations';
  /** Controlled phone drawer, so a product tour can open the navigation before a step. */
  navOpen?: boolean;
  onNavOpenChange?: (open: boolean) => void;
  tabs?: ShellTabs;
  children: ReactNode;
}) {
  const [ownOpen, setOwnOpen] = useState(false);
  const open = navOpen ?? ownOpen;
  const setOpen = (next: boolean) => {
    setOwnOpen(next);
    onNavOpenChange?.(next);
  };
  const close = () => setOpen(false);
  return (
    <div
      className={tabs ? 'shell has-tabs' : 'shell'}
      data-theme={theme === 'novations' ? 'novations' : undefined}
    >
      <a className="skip" href="#main">
        Skip to main content
      </a>
      {sidebar(undefined, 'side')}
      <div>
        <Topbar onMenu={() => setOpen(true)} title={title} brand={tabs?.brand}>
          {topbar}
        </Topbar>
        <main id="main" tabIndex={-1}>
          {children}
        </main>
      </div>
      {tabs && <TabBar tabs={tabs} moreOpen={open} onMore={() => setOpen(!open)} />}
      <Drawer open={open} label="Menu" side={tabs ? 'bottom' : 'left'} onClose={close}>
        {sidebar(close, tabs ? 'sheet' : 'side')}
      </Drawer>
    </div>
  );
}

export function Page({
  title,
  subtitle,
  actions,
  children,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>{title}</h1>
          {subtitle && <p className="sub">{subtitle}</p>}
        </div>
        {actions && <div className="row">{actions}</div>}
      </div>
      {children}
    </div>
  );
}
